import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

const getFormatConfig = (format) => {
  const normalizedFormat = format.toLowerCase();
  return {
    json: {
      contentType: 'application/json',
      extension: 'json',
      name: 'JSON'
    },
    csv: {
      contentType: 'text/csv',
      extension: 'csv',
      name: 'CSV'
    }
  }[normalizedFormat] || {
    contentType: 'text/csv',
    extension: 'csv',
    name: 'CSV'
  };
};

const createDownloadLink = (blob, contentType, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const sanitizeFilename = (name, id, extension) => {
  const sanitized = name ? name.replace(/[^a-z0-9]/gi, '_') : `export_${id}`;
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `${sanitized}_${timestamp}.${extension}`;
};

const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  "Access-Control-Allow-Origin": "*",
  Authorization: `Bearer ${getToken()}`,
});

// Async Thunks
export const fetchExports = createAsyncThunk(
  'exports/fetchExports',
  async ({ searchQuery = "", page = 0, rowsPerPage = 10, selectedAccount }, { rejectWithValue }) => {
    const url = `${apiBaseUrl}/exports?account_id=${selectedAccount}&page=${page + 1}&per_page=${rowsPerPage}&q=${encodeURIComponent(searchQuery)}`;

    try {
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const fetchPendingApprovalExports = createAsyncThunk(
  "exports/fetchPendingApprovalExports",
  async (
    { searchQuery = "", page = 0, rowsPerPage = 10, selectedAccount, onlyApproved = false },
    { rejectWithValue }
  ) => {
    const url = `${apiBaseUrl}/exports/pending_approval_exports?account_id=${selectedAccount}&page=${page + 1}&per_page=${rowsPerPage}&q=${encodeURIComponent(searchQuery)}&only_approved=${onlyApproved}`;
    try {
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const downloadExport = createAsyncThunk(
  'exports/downloadExport',
  async ({ id, selectedAccount, format = 'csv' }, { rejectWithValue }) => {
    try {
      const formatConfig = getFormatConfig(format);
      const headers = getAuthHeaders();
      // Use exam_function_fields endpoint with export_id parameter
      const downloadUrl = `${apiBaseUrl}/exports/exam_function_fields?export_id=${id}&account_id=${selectedAccount}&format=${format}`;
      const response = await axios.post(downloadUrl, null, {
        headers,
        responseType: 'blob',
      });

      const filename = getFilenameFromHeader(
        response.headers['content-disposition']
      );
      createDownloadLink(response.data, formatConfig.contentType, filename);
      
      toast.success(`${formatConfig.name} export downloaded successfully`);
      return true;
    } catch (error) {
      const formatConfig = getFormatConfig(format);
      toast.error(`Failed to download ${formatConfig.name} export`);
      return rejectWithValue(error.response?.data || error.message || "An error occurred");
    }
  }
);

export const fetchExport = createAsyncThunk(
  'exports/fetchExport',
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    const url = `${apiBaseUrl}/exports/${id}/edit?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error.response);
    }
  }
);

export const createExport = createAsyncThunk(
  'exports/createExport',
  async ({ exportData, selectedAccount }, { rejectWithValue }) => {
    const url = `${apiBaseUrl}/exports?account_id=${selectedAccount}`;
    
    try {
      const response = await axios.post(url, { export: exportData }, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create export");
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const updateExport = createAsyncThunk(
  'exports/updateExport',
  async ({ id, exportData, selectedAccount }, { rejectWithValue }) => {
    const url = `${apiBaseUrl}/exports/${id}?account_id=${selectedAccount}`;
    
    try {
      const response = await axios.patch(url, { export: exportData }, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update export");
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const deleteExport = createAsyncThunk(
  'exports/deleteExport',
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    const url = `${apiBaseUrl}/exports/${id}?account_id=${selectedAccount}`;
    try {
      await axios.delete(url, { headers: getAuthHeaders() });
      toast.success("Export deleted successfully");
      return id;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete export");
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const copyExport = createAsyncThunk(
  'exports/copyExport',
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    const url = `${apiBaseUrl}/exports/${id}/copy_export?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(url, null, {
        headers: getAuthHeaders(),
      });
      toast.success("Export copied successfully");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to copy export");
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const updateExportStatus = createAsyncThunk(
  'exports/updateExportStatus',
  async ({ id, status, approvalStatus, selectedAccount }, { rejectWithValue }) => {
    const url = `${apiBaseUrl}/exports/${id}?account_id=${selectedAccount}`;
    try {
      const payload = { status };
      if (approvalStatus) payload.approval_status = approvalStatus;
      const response = await axios.patch(
        url,
        { export: payload },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const fetchPublicExports = createAsyncThunk(
  'exports/fetchPublicExports',
  async ({ searchQuery = "", page = 0, rowsPerPage = 10, selectedAccount }, { rejectWithValue }) => {
    const url = `${apiBaseUrl}/public_exports?account_id=${selectedAccount}&page=${page + 1}&per_page=${rowsPerPage}&q=${encodeURIComponent(searchQuery)}`;
    try {
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const fetchPublicExport = createAsyncThunk(
  'exports/fetchPublicExport',
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    const url = `${apiBaseUrl}/public_exports/${id}?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error.response);
    }
  }
);

export const createPublicExport = createAsyncThunk(
  'exports/createPublicExport',
  async ({ id, name, groupIds, selectedAccount }, { rejectWithValue }) => {
    const url = `${apiBaseUrl}/public_exports?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(
        url,
        { id, name, group_ids: groupIds || [] },
        { headers: getAuthHeaders() }
      );
      toast.success("Export added to your exports");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add export");
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

// Slice
const exportSlice = createSlice({
  name: 'export',
  initialState: {
    exportsList: [],
    currentExport: null,
    totalExportsCount: 0,
    isLoading: false,
    error: null,
    publicExportsList: [],
    publicExportsTotalCount: 0,
    publicExportsLoading: false,
    currentPublicExport: null,
  },
  reducers: {
    clearCurrentExport: (state) => {
      state.currentExport = null;
    },
    clearCurrentPublicExport: (state) => {
      state.currentPublicExport = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Exports
      .addCase(fetchExports.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchExports.fulfilled, (state, action) => {
        state.exportsList = action.payload.exports || [];
        state.totalExportsCount = action.payload.meta?.total_count || 0;
        state.isLoading = false;
      })
      .addCase(fetchExports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPendingApprovalExports.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPendingApprovalExports.fulfilled, (state, action) => {
        state.exportsList = action.payload.exports || [];
        state.totalExportsCount = action.payload.meta?.total_count || 0;
        state.isLoading = false;
      })
      .addCase(fetchPendingApprovalExports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch Single Export
      .addCase(fetchExport.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchExport.fulfilled, (state, action) => {
        state.currentExport = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchExport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create Export
      .addCase(createExport.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createExport.fulfilled, (state, action) => {
        state.exportsList.unshift(action.payload);
        state.totalExportsCount += 1;
        state.isLoading = false;
      })
      .addCase(createExport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(createPublicExport.fulfilled, (state, action) => {
        state.exportsList.unshift(action.payload);
        state.totalExportsCount += 1;
      })
      .addCase(createPublicExport.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Update Export
      .addCase(updateExport.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateExport.fulfilled, (state, action) => {
        const index = state.exportsList.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.exportsList[index] = action.payload;
        }
        if (state.currentExport?.id === action.payload.id) {
          state.currentExport = action.payload;
        }
        state.isLoading = false;
      })
      .addCase(updateExport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Delete Export
      .addCase(deleteExport.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteExport.fulfilled, (state, action) => {
        state.exportsList = state.exportsList.filter((item) => item.id !== action.payload);
        if (state.currentExport?.id === action.payload) {
          state.currentExport = null;
        }
        state.isLoading = false;
      })
      .addCase(deleteExport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(copyExport.fulfilled, (state, action) => {
        state.exportsList.unshift(action.payload);
        state.totalExportsCount += 1;
      })
      .addCase(copyExport.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateExportStatus.fulfilled, (state, action) => {
        const updatedExport = action.payload;
        const index = state.exportsList.findIndex(
          (item) => item.id === updatedExport.id
        );
        if (index !== -1) {
          state.exportsList[index] = updatedExport;
        }
        if (state.currentExport?.id === updatedExport.id) {
          state.currentExport = updatedExport;
        }
      })
      .addCase(updateExportStatus.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Fetch Public Exports (list)
      .addCase(fetchPublicExports.pending, (state) => {
        state.publicExportsLoading = true;
      })
      .addCase(fetchPublicExports.fulfilled, (state, action) => {
        state.publicExportsList = action.payload.exports || [];
        state.publicExportsTotalCount = action.payload.meta?.total_count || 0;
        state.publicExportsLoading = false;
      })
      .addCase(fetchPublicExports.rejected, (state, action) => {
        state.publicExportsLoading = false;
        state.error = action.payload;
      })

      // Fetch Single Public Export (for view)
      .addCase(fetchPublicExport.pending, (state) => {
        state.publicExportsLoading = true;
      })
      .addCase(fetchPublicExport.fulfilled, (state, action) => {
        state.currentPublicExport = action.payload;
        state.publicExportsLoading = false;
      })
      .addCase(fetchPublicExport.rejected, (state, action) => {
        state.publicExportsLoading = false;
        state.currentPublicExport = null;
        state.error = action.payload;
      });
  },
});

const getFilenameFromHeader = (contentDisposition, extension = 'csv') => {
  const fallback = `export_${Date.now()}.${extension}`;

  if (!contentDisposition) return fallback;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
  if (utf8Match) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = contentDisposition.match(/filename="([^"]+)"/);
  if (asciiMatch) {
    return asciiMatch[1];
  }

  return fallback;
};

export const { clearCurrentExport, clearCurrentPublicExport } = exportSlice.actions;
export default exportSlice.reducer;

